import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Grid, Stack } from '@ltvco/refresh-lib/theme';
import { FcraWarning, SearchData } from '@ltvco/refresh-lib/v1';
import { WhereIsMyVINModal } from '@ltvco/refresh-lib/v2';
import React, { useState, useEffect } from 'react';

import {
  DisclaimerText,
  HeaderText,
  SearchHubContainer,
  StyledTabDesktop,
  StyledTabsDesktop,
} from './components';
import {
  SearchTabType,
  getPlaceholderText,
  renderSearchForm,
  SubheaderText,
} from './utils';

interface DesktopSearchHubProps {
  // eslint-disable-next-line no-unused-vars
  onSearchSuccess: (data: SearchData) => void;
  placement?: string;
  searchTabs: SearchTabType[];
  activeTab?: string;
  // eslint-disable-next-line no-unused-vars
  onTabChange?: (event: React.SyntheticEvent, newValue: string) => void;
}

export const DesktopSearchHub: React.FC<DesktopSearchHubProps> = ({
  onSearchSuccess,
  placement = 'dashboard',
  searchTabs,
  activeTab,
  onTabChange,
}) => {
  const [placeholderText, setPlaceholderText] = useState<string>('');
  const [subheaderText, setSubheaderText] = useState<string>('');
  const [isVINModalOpen, setIsVINModalOpen] = useState<boolean>(false);
  const {
    data: { brandName },
    links: { fcraLink, tosLink },
  } = useAppConstantsContext();

  const displayDisclaimer = activeTab === 'peopleTab';
  const isVehicleTab = activeTab === 'vehicleTab';

  useEffect(() => {
    if (searchTabs.length > 0) {
      updateTabContent(activeTab, searchTabs);
    }
  }, [activeTab, searchTabs]);

  const updateTabContent = (tabId: string = '', tabs: SearchTabType[]) => {
    const activeTabData = tabs.find((tab) => tab.id === tabId);
    if (activeTabData) {
      setSubheaderText(activeTabData.subheaderText);
      setPlaceholderText(getPlaceholderText(tabId));
    }
  };

  return (
    <SearchHubContainer>
      <HeaderText variant="h1">What will you search for?</HeaderText>
      <Stack
        sx={{
          direction: 'column',
          justifyContent: 'center',
          position: 'relative',
          marginY: 2,
          paddingBottom: 2,
        }}
      >
        <SubheaderText
          subheaderText={subheaderText}
          isPhoneTab={activeTab === 'phoneTab'}
          isVehicleTab={isVehicleTab}
          setIsVINModalOpen={setIsVINModalOpen}
        />

        <Grid container justifyContent="center" marginTop={2}>
          <Grid size={{ xs: 12 }}>
            <StyledTabsDesktop
              value={activeTab}
              onChange={onTabChange}
              variant="standard"
              scrollButtons="auto"
              centered={false}
            >
              {searchTabs.map((tab) => (
                <StyledTabDesktop
                  key={tab.id}
                  value={tab.id}
                  label={tab.displayName}
                  icon={tab.icon}
                  iconPosition="start"
                />
              ))}
            </StyledTabsDesktop>
          </Grid>
        </Grid>

        {renderSearchForm(
          activeTab,
          placeholderText,
          onSearchSuccess,
          placement,
          false
        )}
      </Stack>

      {displayDisclaimer && (
        <DisclaimerText>
          <FcraWarning
            fcraLink={fcraLink}
            tosLink={tosLink}
            brandName={brandName}
          />
        </DisclaimerText>
      )}

      <WhereIsMyVINModal
        isOpen={isVINModalOpen}
        handleClose={() => {
          setIsVINModalOpen(false);
        }}
      />
    </SearchHubContainer>
  );
};
